import { useEffect, useState } from "react"
import { Stack } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { htmlToText } from "lib/html-to-text"
import { useIntroShareLinkCommentReason } from "routes/schedule/plugins/share-link-comments/use-intro-share-link-comment-reason"
import { useAutoJustifyFromComment } from "./use-auto-justify-from-comment"
import { formatCreateSubjectAudit } from "./lib/format-create-subject-audit"
import { IntroJustifyButton } from "./intro-justify-button"
import { IntroEditJustification } from "./intro-edit-justification"
import { ViewJustification } from "./view-justification"
import { useOptimisticTailoringAudit } from "./hooks/use-optimistic-tailoring-audit"

// One intro subject's justification (introduction body or its notes), inside the loaded-audits Bound.
// "Tailored" here means the host has set a content override for this subject. Renders the justify button
// + card + editor (reusing the task pieces) and auto-saves the comment-derived justification on change.
export function IntroSubjectJustificationBody() {
    const {
        tailoredAudits,
        payload,
        schedule,
        scheduleItem,
        tailoringReferenceId,
        isEditable = true,
        subjectId,
        subjectTitle,
        part,
        overrideField,
    } = useBoundContext()
    const [justification, setJustification] = useState(null)
    const [tailoringAudit, setTailoringAudit] = useOptimisticTailoringAudit(tailoredAudits?.[subjectId])
    const tailored = htmlToText(payload?.[overrideField] ?? "").trim().length > 0
    const commentReason = useIntroShareLinkCommentReason(part)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(clearWhenUntailored, [tailored])

    useAutoJustifyFromComment({
        subjectKey: subjectId,
        tailored,
        isEditable,
        hasComment: !!commentReason,
        hasExistingReason: !!tailoringAudit?.payload?.reason,
        buildAudit: buildAudit,
    })

    return (
        <Bound
            tailoringAudit={tailoringAudit}
            setTailoringAudit={setTailoringAudit}
            justification={justification}
            setJustification={setJustification}
            tailored={tailored}
            commentReason={commentReason}
        >
            <IntroJustifyButton />
            <Stack spacing={1}>
                <If value={justification}>
                    <IntroEditJustification />
                    <Else>
                        <If value={!!tailoringAudit?.payload?.reason}>
                            <ViewJustification />
                        </If>
                    </Else>
                </If>
            </Stack>
        </Bound>
    )

    function clearWhenUntailored() {
        if (!tailored) setJustification(null)
    }

    function buildAudit() {
        return formatCreateSubjectAudit({
            subjectId,
            subjectTitle,
            tailoringReferenceId,
            schedule,
            scheduleItem,
            reason: commentReason,
        })
    }
}
